import { useEffect, useState, useMemo } from 'react';
import type { ReactNode } from 'react';
import { getIncidents, getIncidentDetails, createSubscription } from '@generated/server/incidents';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import { Button } from '@components/ui/button';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Wrench,
  Clock,
  Mail,
  ChevronDown,
  ChevronUp,
  Bell,
  BellOff,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Search,
  Filter,
  History,
  FileText,
  Share2,
  Check
} from 'lucide-react';
import { cn } from '@lib/utils';

type Incident = Awaited<ReturnType<typeof getIncidents>>['items'][number];
type IncidentDetail = Awaited<ReturnType<typeof getIncidentDetails>>;

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Unknown';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDateFull(dateStr: string | null | Date): string {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const day = date.getDate();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}, ${hours}:${minutes} AEST`;
}

function formatLastIncidentTime(dateStr: string | null | Date): string {
  if (!dateStr) return 'No incidents recorded';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'No incidents recorded';
  
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const day = date.getDate();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `Last incident at ${hours}:${minutes} AEST - ${day} ${month} ${year}`;
}

function getStatusColor(status: string | null, serviceStatus: string | null): string {
  if (status === 'Resolved' || serviceStatus === 'Resolved') return 'bg-emerald-500';
  if (status === 'Investigating') return 'bg-rose-500';
  if (status === 'Monitoring' || serviceStatus === 'Degraded') return 'bg-amber-500';
  if (status === 'Maintenance') return 'bg-sky-500';
  return 'bg-slate-400';
}

function getStatusIcon(status: string | null, serviceStatus: string | null) {
  if (status === 'Resolved' || serviceStatus === 'Resolved') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
  if (status === 'Investigating') return <XCircle className="h-4 w-4 text-rose-500" />;
  if (status === 'Monitoring' || serviceStatus === 'Degraded') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  if (status === 'Maintenance') return <Wrench className="h-4 w-4 text-sky-500" />;
  return <Clock className="h-4 w-4 text-slate-400" />;
}

function getStatusLabel(status: string | null, serviceStatus: string | null): string {
  if (status === 'Resolved' || serviceStatus === 'Resolved') return 'Resolved';
  if (status === 'Investigating') return 'Outage';
  if (status === 'Monitoring') return 'Monitoring';
  if (serviceStatus === 'Degraded') return 'Degraded';
  if (status === 'Maintenance') return 'Maintenance';
  if (status === 'Reported') return 'Reported';
  return 'Operational';
}

function getOverallStatus(incidents: Incident[]): { label: string; bgClass: string; textClass: string; icon: ReactNode } {
  const activeIncidents = incidents.filter(i => i.status !== 'Resolved' && i.serviceStatus !== 'Resolved');
  
  // Check for currently active in-progress maintenance (now is between start and end time)
  const now = new Date();
  const maintenanceInProgress = activeIncidents.some(i => {
    const isMaint = i.status === 'Maintenance' || i.serviceStatus === 'Maintence' || i.serviceStatus === 'Maintenance';
    if (!isMaint) return false;
    if (!i.incidentStart) return true; // If no start date specified, treat as active
    const start = new Date(i.incidentStart);
    if (now < start) return false; // Starts in the future -> not in progress yet
    if (i.incidentEnd) {
      const end = new Date(i.incidentEnd);
      if (now > end) return false; // Already finished
    }
    return true; // Now is >= start date
  });

  const activeOutagesOrDegradations = activeIncidents.filter(i => {
    const isMaint = i.status === 'Maintenance' || i.serviceStatus === 'Maintence' || i.serviceStatus === 'Maintenance';
    return !isMaint;
  });

  if (activeOutagesOrDegradations.length === 0 && !maintenanceInProgress) {
    return {
      label: 'All Systems Operational',
      bgClass: 'bg-emerald-500 text-white',
      textClass: 'text-emerald-600',
      icon: <CheckCircle2 className="h-6 w-6 text-white" />
    };
  }

  const hasOutage = activeOutagesOrDegradations.some(i => i.status === 'Investigating');
  if (hasOutage) {
    return {
      label: 'Service Disruption Reported',
      bgClass: 'bg-rose-600 text-white',
      textClass: 'text-rose-600',
      icon: <XCircle className="h-6 w-6 text-white" />
    };
  }

  const hasDegraded = activeOutagesOrDegradations.some(i => i.status === 'Monitoring' || i.serviceStatus === 'Degraded');
  if (hasDegraded) {
    return {
      label: 'Partial Service Degradation',
      bgClass: 'bg-amber-500 text-white',
      textClass: 'text-amber-600',
      icon: <AlertTriangle className="h-6 w-6 text-white" />
    };
  }

  if (maintenanceInProgress) {
    return {
      label: 'Scheduled Maintenance in Progress',
      bgClass: 'bg-sky-600 text-white',
      textClass: 'text-sky-600',
      icon: <Wrench className="h-6 w-6 text-white" />
    };
  }

  return {
    label: 'All Systems Operational',
    bgClass: 'bg-emerald-500 text-white',
    textClass: 'text-emerald-600',
    icon: <CheckCircle2 className="h-6 w-6 text-white" />
  };
}

export default function App() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  const [incidentDetail, setIncidentDetail] = useState<IncidentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Check URL parameters for direct incident links (?incident=XYZ or ?incidentId=XYZ or ?id=XYZ)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const incidentParam = params.get('incident') || params.get('incidentId') || params.get('id');
    if (incidentParam) {
      setSelectedIncidentId(incidentParam);
    }
  }, []);

  const handleCopyDirectLink = (id: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('incidentId', id);
    navigator.clipboard.writeText(url.toString());
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };
  
  // Subscription form state
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [subscribeFirstName, setSubscribeFirstName] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribePhone, setSubscribePhone] = useState('');
  const [selectedServices, setSelectedServices] = useState<Record<string, string[]>>({});
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeIsUpdate, setSubscribeIsUpdate] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);

  // View state: 'dashboard' or 'history' (Full page table)
  const [currentView, setCurrentView] = useState<'dashboard' | 'history'>('dashboard');

  // History table filters & search
  const [historySearch, setHistorySearch] = useState('');
  const [historyServiceFilter, setHistoryServiceFilter] = useState<string>('ALL');
  const [historyStatusFilter, setHistoryStatusFilter] = useState<string>('ALL');

  // Muted services tracking for user notification preferences
  const [mutedServices, setMutedServices] = useState<Record<string, boolean>>({});
  // Expanded service cards state
  const [expandedServices, setExpandedServices] = useState<Record<string, boolean>>({});

  // Hardcoded permanent core services
  const CORE_SERVICES = useMemo(() => ['VoIP', 'NBN', 'Mobile'], []);

  // Map of default sub-services derived from the board's Sub Service Type dropdown
  const DEFAULT_SUB_SERVICES: Record<string, string[]> = useMemo(() => ({
    'VoIP': [
      'Hosted PBX',
      'Outbound Calling',
      'Inbound Calling',
      'SMS Services',
      'Virtual Fax Services',
      'Web Portal'
    ],
    'NBN': [
      'Australian Capital Territory',
      'New South Wales',
      'Northern Territory',
      'Queensland',
      'South Australia',
      'Tasmania',
      'Victoria',
      'Western Australia'
    ],
    'Mobile': [
      'Mobile Network',
      'Voice & Data',
      'International Roaming'
    ]
  }), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getIncidents()
      .then((res) => {
        if (active) {
          setIncidents(res?.items ?? []);
        }
      })
      .catch((e: Error) => {
        if (active) setError(e);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedIncidentId) {
      setIncidentDetail(null);
      return;
    }

    let active = true;
    setDetailLoading(true);
    getIncidentDetails({ data: { itemId: selectedIncidentId } })
      .then((res) => {
        if (active && res) setIncidentDetail(res);
      })
      .catch((e: Error) => {
        console.error('Failed to load incident details', e);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => { active = false; };
  }, [selectedIncidentId]);

  const toggleMuteService = (serviceName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMutedServices(prev => ({
      ...prev,
      [serviceName]: !prev[serviceName]
    }));
  };

  const toggleExpandService = (serviceName: string) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceName]: !prev[serviceName]
    }));
  };

  const toggleServiceSubscription = (service: string, subService?: string) => {
    setSelectedServices(prev => {
      const current = { ...prev };
      if (!current[service]) {
        current[service] = [];
      }
      
      if (subService) {
        // Toggle sub-service
        const index = current[service].indexOf(subService);
        if (index >= 0) {
          current[service].splice(index, 1);
        } else {
          current[service].push(subService);
        }
        // Remove service if no sub-services selected
        if (current[service].length === 0) {
          delete current[service];
        }
      } else {
        // Toggle entire service
        if (current[service].length === 0) {
          // Select service without sub-services
          current[service] = ['__ALL__'];
        } else {
          // Deselect service
          delete current[service];
        }
      }
      
      return current;
    });
  };

  const handleSubscribe = async () => {
    setSubscribeError(null);
    
    // Validation
    if (!subscribeFirstName.trim()) {
      setSubscribeError('First name is required');
      return;
    }
    
    if (!subscribeEmail.trim() && !subscribePhone.trim()) {
      setSubscribeError('Please provide either an email or phone number');
      return;
    }
    
    if (subscribeEmail && !subscribeEmail.includes('@')) {
      setSubscribeError('Please provide a valid email address');
      return;
    }
    
    if (Object.keys(selectedServices).length === 0) {
      setSubscribeError('Please select at least one service to subscribe to');
      return;
    }
    
    setSubscribing(true);
    
    try {
      // Format services for the API
      const services = Object.entries(selectedServices).map(([serviceType, subServices]) => ({
        serviceType,
        subServiceTypes: subServices.filter(s => s !== '__ALL__')
      }));
      
      const result = await createSubscription({
        data: {
          firstName: subscribeFirstName.trim(),
          email: subscribeEmail.trim() || undefined,
          phoneNumber: subscribePhone.trim() || undefined,
          services
        }
      });
      
      setSubscribeIsUpdate(result.isUpdate ?? false);
      setSubscribeSuccess(true);
      
      // Reset form after 2.5 seconds
      setTimeout(() => {
        setShowSubscribe(false);
        setSubscribeSuccess(false);
        setSubscribeIsUpdate(false);
        setSubscribeFirstName('');
        setSubscribeEmail('');
        setSubscribePhone('');
        setSelectedServices({});
      }, 2500);
      
    } catch (e) {
      console.error('Subscription failed:', e);
      setSubscribeError(e instanceof Error ? e.message : 'Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const activeIncidents = useMemo(() => {
    return incidents.filter(i => {
      const isResolved = i.status === 'Resolved' || i.serviceStatus === 'Resolved';
      const isMaintenance = i.status === 'Maintenance' || i.serviceStatus === 'Maintence' || i.serviceStatus === 'Maintenance';
      return !isResolved && !isMaintenance;
    });
  }, [incidents]);

  const activeMaintenance = useMemo(() => {
    return incidents.filter(i => {
      const isResolved = i.status === 'Resolved' || i.serviceStatus === 'Resolved';
      const isMaintenance = i.status === 'Maintenance' || i.serviceStatus === 'Maintence' || i.serviceStatus === 'Maintenance';
      return !isResolved && isMaintenance;
    });
  }, [incidents]);

  const latestIncident = useMemo(() => {
    if (incidents.length === 0) return null;
    return incidents[0]; // Ordered desc by incidentStart
  }, [incidents]);

  const overallStatus = getOverallStatus(incidents);

  // Parse and group services & sub-services
  const serviceMap = useMemo(() => {
    const map: Record<string, {
      activeIncidents: Incident[];
      subServices: Record<string, Incident[]>;
    }> = {};

    // Initialize map with hardcoded core services & default sub-services
    CORE_SERVICES.forEach(svc => {
      map[svc] = {
        activeIncidents: [],
        subServices: {}
      };
      const defaults = DEFAULT_SUB_SERVICES[svc] ?? [];
      defaults.forEach(sub => {
        map[svc].subServices[sub] = [];
      });
    });

    // Process incidents
    incidents.forEach(incident => {
      const isResolved = incident.status === 'Resolved' || incident.serviceStatus === 'Resolved';
      const isMaintenance = incident.status === 'Maintenance' || incident.serviceStatus === 'Maintence' || incident.serviceStatus === 'Maintenance';

      let isIncidentActive = !isResolved;
      if (isMaintenance) {
        // Maintenance ONLY impacts service health/status if current time is actively between start and end time
        const now = new Date();
        if (incident.incidentStart) {
          const start = new Date(incident.incidentStart);
          if (now < start) {
            isIncidentActive = false; // Scheduled in the future -> does NOT mark service degraded
          }
        }
        if (incident.incidentEnd) {
          const end = new Date(incident.incidentEnd);
          if (now > end) {
            isIncidentActive = false; // Maintenance completed
          }
        }
      }
      
      const rawServices = incident.serviceType ?? [];
      const rawSubServices = incident.subServiceType ?? [];

      // Loop through affected service types
      rawServices.forEach(svc => {
        if (!map[svc]) {
          map[svc] = { activeIncidents: [], subServices: {} };
        }

        if (isIncidentActive) {
          map[svc].activeIncidents.push(incident);
        }

        // Process sub-services
        rawSubServices.forEach(rawSub => {
          let targetSub = rawSub;
          // Support "NBN - Victoria", "VOIP - Hosted PBX", "SMS - SMS Services"
          if (rawSub.includes(' - ')) {
            const parts = rawSub.split(' - ');
            targetSub = parts[parts.length - 1].trim();
          } else {
            targetSub = rawSub.trim();
          }

          if (!map[svc].subServices[targetSub]) {
            map[svc].subServices[targetSub] = [];
          }
          if (isIncidentActive) {
            map[svc].subServices[targetSub].push(incident);
          }
        });
      });
    });

    return map;
  }, [incidents]);

  const serviceNames = CORE_SERVICES;

  // Filtered incidents for 6-month full page table history
  const filteredHistoryIncidents = useMemo(() => {
    return incidents.filter(incident => {
      // Search filter
      if (historySearch.trim()) {
        const query = historySearch.toLowerCase();
        const matchesName = incident.name?.toLowerCase().includes(query);
        const matchesService = incident.serviceType?.some(st => st.toLowerCase().includes(query));
        const matchesSubService = incident.subServiceType?.some(sub => sub.toLowerCase().includes(query));
        const matchesUpdates = incident.updates?.some(u => u.text_body?.toLowerCase().includes(query));
        
        if (!matchesName && !matchesService && !matchesSubService && !matchesUpdates) {
          return false;
        }
      }

      // Service filter
      if (historyServiceFilter !== 'ALL') {
        const hasService = incident.serviceType?.includes(historyServiceFilter);
        if (!hasService) return false;
      }

      // Status filter
      if (historyStatusFilter !== 'ALL') {
        const label = getStatusLabel(incident.status, incident.serviceStatus);
        if (historyStatusFilter === 'Outage' && label !== 'Outage') return false;
        if (historyStatusFilter === 'Degraded' && label !== 'Degraded Performance' && label !== 'Degraded') return false;
        if (historyStatusFilter === 'Maintenance' && label !== 'Under Maintenance' && label !== 'Maintenance') return false;
        if (historyStatusFilter === 'Resolved' && label !== 'Resolved' && label !== 'Operational') return false;
      }

      return true;
    });
  }, [incidents, historySearch, historyServiceFilter, historyStatusFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="h-9 w-9 border-4 border-[#072340] border-t-[#80CCB8] rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Checking service status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md bg-white dark:bg-slate-900 p-8 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <XCircle className="h-12 w-12 text-rose-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Service Status Unavailable</h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
            Unable to fetch status data. Please check your network connection and refresh.
          </p>
          <Button onClick={() => window.location.reload()}>Refresh Page</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans">
      {/* Top Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-4">
        <div className="container mx-auto px-4 max-w-5xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a
              href="https://www.upstreamtech.io"
              target="_blank"
            rel="noopener noreferrer"
              className="focus:outline-hidden group"
                >
                <img
                src="https://files-public.monday.com/apse2/542e471e-5728-4290-928f-3c68f44b7e23/Upstream-Flat-Wordmark-RGB.png"
              alt="Upstream Logo"
            className="h-7 w-auto object-contain dark:brightness-200"
          />
                  </a>
                </div>

              <div className="flex items-center gap-3">
            {currentView === 'history' ? (
          <Button
      variant="outline"
          size="sm"
            onClick={() => setCurrentView('dashboard')}
            className="gap-2 text-xs sm:text-sm font-medium"
              >
                ← System Status
                </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSubscribe(!showSubscribe)}
                    className="gap-2 text-xs sm:text-sm font-medium"
                  >
                    <Mail className="h-4 w-4" />
                    Subscribe
                  </Button>
                )}
                </div>
                </div>
                  </header>

      {/* Main Container */}
      <main className="container mx-auto px-4 py-8 max-w-5xl space-y-8">
        
        {/* VIEW 1: DASHBOARD */}
        {currentView === 'dashboard' && (
          <>
            {/* Overall Status Banner */}
            <div className={cn("rounded-xl p-5 shadow-sm transition-all flex items-center justify-between gap-4", overallStatus.bgClass)}>
              <div className="flex items-center gap-3.5">
                <div className="p-2 rounded-lg bg-white/10 backdrop-blur-xs">
                  {overallStatus.icon}
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold tracking-tight">{overallStatus.label}</h2>
                  <p className="text-xs sm:text-sm text-white/90 mt-0.5">
                    {activeIncidents.length === 0
                      ? 'All core services and regional sub-services are operating smoothly.'
                      : `${activeIncidents.length} active incident${activeIncidents.length > 1 ? 's' : ''} currently affecting services.`}
                  </p>
                </div>
              </div>
            </div>

            {/* Comprehensive Subscription Form */}
            {showSubscribe && (
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 shadow-sm transition-all">
                {subscribeSuccess ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-3" />
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-1">
                      {subscribeIsUpdate ? 'Subscription Updated!' : 'Successfully Subscribed!'}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {subscribeIsUpdate 
                        ? 'Your notification preferences have been updated.'
                        : 'You\'ll receive notifications for the selected services.'}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <h3 className="font-semibold text-base text-slate-900 dark:text-white">Subscribe to Service Notifications</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          Get instant updates about outages and maintenance for selected services
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setShowSubscribe(false);
                          setSubscribeError(null);
                        }}
                        className="text-slate-400 hover:text-slate-600 text-xs"
                      >
                        ✕
                      </button>
                    </div>

                    {subscribeError && (
                      <div className="mb-4 p-3 bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900 rounded-lg text-sm text-rose-700 dark:text-rose-400">
                        {subscribeError}
                      </div>
                    )}

                    <div className="space-y-4">
                      {/* First Name */}
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                          First Name <span className="text-rose-500">*</span>
                        </label>
                        <Input
                          type="text"
                          placeholder="Your first name"
                          value={subscribeFirstName}
                          onChange={(e) => setSubscribeFirstName(e.target.value)}
                          className="text-sm bg-slate-50 dark:bg-slate-800/50"
                        />
                      </div>

                      {/* Email and Phone */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                            Email Address
                          </label>
                          <Input
                            type="email"
                            placeholder="your.email@company.com"
                            value={subscribeEmail}
                            onChange={(e) => setSubscribeEmail(e.target.value)}
                            className="text-sm bg-slate-50 dark:bg-slate-800/50"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                            Mobile Number (AU)
                          </label>
                          <Input
                            type="tel"
                            placeholder="0412 345 678"
                            value={subscribePhone}
                            onChange={(e) => setSubscribePhone(e.target.value)}
                            className="text-sm bg-slate-50 dark:bg-slate-800/50"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
                        * Provide at least one contact method (email or phone)
                      </p>

                      {/* Service Selection */}
                      <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Select Services to Monitor <span className="text-rose-500">*</span>
                        </label>
                        <div className="space-y-2 max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50 dark:bg-slate-800/30">
                          {CORE_SERVICES.map(service => {
                            const subServices = DEFAULT_SUB_SERVICES[service] ?? [];
                            const isServiceSelected = !!selectedServices[service];
                            const selectedSubCount = selectedServices[service]?.filter(s => s !== '__ALL__').length ?? 0;
                            
                            return (
                              <div key={service} className="border-b border-slate-200 dark:border-slate-700 last:border-0 pb-2 last:pb-0">
                                <div className="flex items-center gap-2 cursor-pointer group">
                                  <input
                                    type="checkbox"
                                    id={`service-${service}`}
                                    checked={isServiceSelected}
                                    onChange={() => toggleServiceSubscription(service)}
                                    className="h-4 w-4 rounded border-slate-300 text-[#80CCB8] focus:ring-[#80CCB8]"
                                  />
                                  <label htmlFor={`service-${service}`} className="font-medium text-sm text-slate-800 dark:text-slate-200 group-hover:text-[#80CCB8] cursor-pointer">
                                    {service}
                                    {selectedSubCount > 0 && (
                                      <span className="ml-1.5 text-xs text-slate-500">({selectedSubCount} regions)</span>
                                    )}
                                  </label>
                                </div>
                                
                                {isServiceSelected && subServices.length > 0 && (
                                  <div className="ml-6 mt-2 space-y-1.5">
                                    <p className="text-xs text-slate-500 mb-1">Select specific regions (optional):</p>
                                    <div className="grid grid-cols-2 gap-1.5">
                                      {subServices.map(subService => (
                                        <div key={subService} className="flex items-center gap-1.5 cursor-pointer text-xs group">
                                          <input
                                            type="checkbox"
                                            id={`sub-${service}-${subService}`}
                                            checked={selectedServices[service]?.includes(subService) ?? false}
                                            onChange={() => toggleServiceSubscription(service, subService)}
                                            className="h-3 w-3 rounded border-slate-300 text-[#80CCB8] focus:ring-[#80CCB8]"
                                          />
                                          <label htmlFor={`sub-${service}-${subService}`} className="text-slate-700 dark:text-slate-300 group-hover:text-[#80CCB8] cursor-pointer">
                                            {subService}
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <Button
                        onClick={handleSubscribe}
                        disabled={subscribing}
                        className="w-full bg-[#80CCB8] hover:bg-[#80CCB8]/90 text-[#072340] font-semibold"
                      >
                        {subscribing ? 'Subscribing...' : 'Subscribe to Notifications'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Active Incidents Alert Section */}
            {activeIncidents.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-rose-600 font-semibold text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Active Incident Updates</span>
                </div>
                <div className="space-y-2.5">
                  {activeIncidents.map((incident) => (
                    <button
                      type="button"
                      key={incident.id}
                      onClick={() => setSelectedIncidentId(incident.id)}
                      className="w-full text-left bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-900/50 hover:border-rose-300 dark:hover:border-rose-700 rounded-xl p-4 shadow-xs cursor-pointer transition-all flex items-start justify-between gap-4"
                    >
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-md bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200">
                            {getStatusLabel(incident.status, incident.serviceStatus)}
                          </span>
                          {incident.serviceType?.map((st) => (
                            <span key={st} className="px-2 py-0.5 text-xs font-medium rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                              {st}
                            </span>
                          ))}
                        </div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-base">
                          {incident.name}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          <span>Updated {formatRelativeTime(incident.updatedAt ?? incident.incidentStart)}</span>
                        </p>
                      </div>
                      <span className="text-xs text-slate-500 font-medium shrink-0 pt-1">
                        Details &rarr;
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Service Type Cards Section (matching provided screenshot design) */}
            <section className="space-y-3">
              <div className="flex items-center justify-end">
                <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                  Past 30 days Uptime
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {serviceNames.map((svcName) => {
                  const svcData = serviceMap[svcName];
                  const activeCount = svcData?.activeIncidents.length ?? 0;
                  const isOperational = activeCount === 0;
                  const isMuted = mutedServices[svcName];
                  const isExpanded = expandedServices[svcName];
                  const subServicesList = Object.keys(svcData?.subServices ?? {});

                  return (
                    <div
                      key={svcName}
                      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-2xs hover:border-slate-300 dark:hover:border-slate-700 transition-all overflow-hidden"
                    >
                      {/* Card Header Row */}
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleExpandService(svcName)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleExpandService(svcName);
                          }
                        }}
                        className="p-3.5 sm:p-4 flex items-center justify-between gap-3 cursor-pointer select-none group"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 transition-colors">
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </span>
                          <h3 className="font-semibold text-sm sm:text-base text-slate-900 dark:text-slate-100 truncate">
                            {svcName}
                          </h3>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {/* 100% Uptime pill if fully operational */}
                          {isOperational && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-900/50">
                              100% Uptime
                            </span>
                          )}

                          {/* Status Icon */}
                          {isOperational ? (
                            <div className="text-emerald-500 dark:text-emerald-400">
                              <CheckCircle2 className="h-5 w-5 fill-emerald-500/10" />
                            </div>
                          ) : (
                            <div className="text-amber-500">
                              <AlertTriangle className="h-5 w-5" />
                            </div>
                          )}

                          {/* Mute/Notification Bell */}
                          <button
                            type="button"
                            onClick={(e) => toggleMuteService(svcName, e)}
                            title={isMuted ? 'Unmute notifications for this service' : 'Mute notifications for this service'}
                            aria-label={isMuted ? `Unmute ${svcName}` : `Mute ${svcName}`}
                            className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                          >
                            {isMuted ? (
                              <BellOff className="h-4 w-4 text-slate-400" />
                            ) : (
                              <Bell className="h-4 w-4 text-slate-500" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Sub-Services List */}
                      {isExpanded && (
                        <div className="border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 p-3 sm:p-4 space-y-2 text-xs">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                            Sub-services & Regional Nodes
                          </p>
                          {subServicesList.length === 0 ? (
                            <p className="text-slate-500 italic py-1">No specific sub-services listed</p>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {subServicesList.map((subName) => {
                                const subIncidents = svcData?.subServices[subName] ?? [];
                                const isSubOperational = subIncidents.length === 0;

                                return (
                                  <div
                                    key={subName}
                                    className="flex items-center justify-between p-2 rounded-md bg-white dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/60"
                                  >
                                    <span className="font-medium text-slate-700 dark:text-slate-300 truncate pr-2">
                                      {subName}
                                    </span>
                                    <span className="inline-flex items-center gap-1 text-[11px]">
                                      {isSubOperational ? (
                                        <>
                                          <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                          <span className="text-emerald-700 dark:text-emerald-400 font-medium">Operational</span>
                                        </>
                                      ) : (
                                        <>
                                          <span className="h-2 w-2 rounded-full bg-amber-500" />
                                          <span className="text-amber-700 dark:text-amber-400 font-medium">Degraded</span>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Scheduled Maintenance & FYI Notices (Blue, placed below services list) */}
            {activeMaintenance.length > 0 && (
              <section className="space-y-3">
                <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400 font-semibold text-sm">
                  <Wrench className="h-4 w-4" />
                  <span>Scheduled Maintenance & FYI Notices</span>
                </div>
                <div className="space-y-2.5">
                  {activeMaintenance.map((maint) => (
                    <button
                      type="button"
                      key={maint.id}
                      onClick={() => setSelectedIncidentId(maint.id)}
                      className="w-full text-left bg-sky-50/60 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900/60 hover:border-sky-300 dark:hover:border-sky-700 rounded-xl p-4 shadow-2xs cursor-pointer transition-all flex items-start justify-between gap-4"
                    >
                      <div className="space-y-1.5 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-md bg-sky-100 text-sky-800 dark:bg-sky-900/80 dark:text-sky-200">
                            Maintenance (FYI)
                          </span>
                          {maint.serviceType?.map((st) => (
                            <span key={st} className="px-2 py-0.5 text-xs font-medium rounded-md bg-white dark:bg-slate-800 text-sky-900 dark:text-sky-200 border border-sky-200/60 dark:border-sky-800">
                              {st}
                            </span>
                          ))}
                        </div>
                        <h3 className="font-semibold text-slate-900 dark:text-slate-100 text-base">
                          {maint.name}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5 flex-wrap">
                          <Clock className="h-3.5 w-3.5 shrink-0" />
                          <span>
                            {maint.incidentStart && new Date(maint.incidentStart) > new Date()
                              ? `Scheduled for ${new Date(maint.incidentStart).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                              : `Logged ${formatRelativeTime(maint.incidentStart ?? maint.createdAt)}`}
                          </span>
                          {maint.incidentEnd && (
                            <>
                              <span className="text-slate-300 dark:text-slate-600">•</span>
                              <span>
                                Planned completion: {new Date(maint.incidentEnd).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                      <span className="text-xs text-sky-700 dark:text-sky-300 font-medium shrink-0 pt-1">
                        Details &rarr;
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Previous Notices & History Link Section (matching screenshot) */}
            <section className="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-2">
              <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100">
                Previous Notices
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                {latestIncident
                  ? formatLastIncidentTime(latestIncident.incidentStart ?? latestIncident.createdAt)
                  : 'No service incidents reported in the last 180 days.'}
              </p>
              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('history');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-[#80CCB8] hover:text-[#80CCB8]/80 hover:underline transition-colors group cursor-pointer"
                >
                  <span>History for upstream</span>
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </section>
          </>
        )}

        {/* VIEW 2: FULL PAGE TABLE 6-MONTH HISTORY */}
        {currentView === 'history' && (
          <div className="space-y-6">
            {/* Navigation Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap pb-2 border-b border-slate-200 dark:border-slate-800">
              <button
                type="button"
                onClick={() => setCurrentView('dashboard')}
                className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to System Status</span>
              </button>

              <span className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full font-medium">
                Last 6 Months (180 Days)
              </span>
            </div>

            {/* Title & Description */}
            <div className="space-y-1">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <History className="h-6 w-6 text-[#80CCB8]" />
                <span>History for Upstream Tech</span>
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Full page ledger of all status updates, service outages, degraded nodes, and maintenance logs over the past 6 months.
              </p>
            </div>

            {/* Quick Metrics Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3.5 rounded-xl">
                <p className="text-xs text-slate-400 font-medium">Total Logged Events</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{incidents.length}</p>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3.5 rounded-xl">
                <p className="text-xs text-slate-400 font-medium">Outages</p>
                <p className="text-xl font-bold text-rose-600 dark:text-rose-400 mt-1">
                  {incidents.filter(i => getStatusLabel(i.status, i.serviceStatus) === 'Outage').length}
                </p>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3.5 rounded-xl">
                <p className="text-xs text-slate-400 font-medium">Degradations</p>
                <p className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                  {incidents.filter(i => getStatusLabel(i.status, i.serviceStatus) === 'Degraded').length}
                </p>
              </div>
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3.5 rounded-xl">
                <p className="text-xs text-slate-400 font-medium">Maintenance</p>
                <p className="text-xl font-bold text-sky-600 dark:text-sky-400 mt-1">
                  {incidents.filter(i => getStatusLabel(i.status, i.serviceStatus) === 'Maintenance').length}
                </p>
              </div>
            </div>

            {/* Search and Filters Bar */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 space-y-3 shadow-2xs">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search input */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search history by title, sub-service, or update notes..."
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    className="pl-9 text-xs sm:text-sm bg-slate-50 dark:bg-slate-800/50"
                  />
                </div>

                {/* Service Filter */}
                <div className="flex items-center gap-2 shrink-0">
                  <Filter className="h-4 w-4 text-slate-400 hidden sm:inline-block" />
                  <select
                    value={historyServiceFilter}
                    onChange={(e) => setHistoryServiceFilter(e.target.value)}
                    className="text-xs sm:text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 text-slate-800 dark:text-slate-200 focus:outline-hidden"
                  >
                    <option value="ALL">All Services</option>
                    {CORE_SERVICES.map(svc => (
                      <option key={svc} value={svc}>{svc}</option>
                    ))}
                  </select>

                  {/* Status Filter */}
                  <select
                    value={historyStatusFilter}
                    onChange={(e) => setHistoryStatusFilter(e.target.value)}
                    className="text-xs sm:text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2 text-slate-800 dark:text-slate-200 focus:outline-hidden"
                  >
                    <option value="ALL">All Statuses</option>
                    <option value="Outage">Outage</option>
                    <option value="Degraded">Degraded</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Resolved">Resolved / Operational</option>
                  </select>
                </div>
              </div>
            </div>

            {/* FULL PAGE HISTORY TABLE */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[700px]">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Date & Time</th>
                      <th className="py-3 px-4">Incident / Event</th>
                      <th className="py-3 px-4">Affected Service & Node</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4">Latest Update Comment</th>
                      <th className="py-3 px-4 text-right">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                    {filteredHistoryIncidents.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-slate-500 dark:text-slate-400">
                          <FileText className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                          <p className="font-semibold text-sm">No status updates match your filter</p>
                          <p className="text-xs mt-1">Try clearing your search query or selecting "All Services".</p>
                        </td>
                      </tr>
                    ) : (
                      filteredHistoryIncidents.map((incident) => {
                        const statusLabel = getStatusLabel(incident.status, incident.serviceStatus);
                        const updates = incident.updates ?? [];
                        const latestUpdate = updates[0]?.text_body;

                        return (
                          <tr
                            key={incident.id}
                            className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors"
                          >
                            {/* Date */}
                            <td className="py-3.5 px-4 font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap">
                              {formatDateFull(incident.incidentStart ?? incident.createdAt)}
                            </td>

                            {/* Title */}
                            <td className="py-3.5 px-4">
                              <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                                {incident.name}
                              </span>
                            </td>

                            {/* Affected Services */}
                            <td className="py-3.5 px-4">
                              <div className="flex flex-wrap items-center gap-1.5">
                                {incident.serviceType?.map((st) => (
                                  <span key={st} className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-[11px] font-medium border border-slate-200/60 dark:border-slate-700/60">
                                    {st}
                                  </span>
                                ))}
                                {incident.subServiceType?.map((sub) => (
                                  <span key={sub} className="px-2 py-0.5 rounded-md bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 text-[11px]">
                                    {sub}
                                  </span>
                                ))}
                              </div>
                            </td>

                            {/* Status */}
                            <td className="py-3.5 px-4 whitespace-nowrap">
                              <span className={cn(
                                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white",
                                getStatusColor(incident.status, incident.serviceStatus)
                              )}>
                                {getStatusIcon(incident.status, incident.serviceStatus)}
                                <span>{statusLabel}</span>
                              </span>
                            </td>

                            {/* Latest Update */}
                            <td className="py-3.5 px-4 max-w-xs truncate text-slate-500 dark:text-slate-400">
                              {latestUpdate ? (
                                <span className="italic" title={latestUpdate}>
                                  "{latestUpdate}"
                                </span>
                              ) : (
                                <span className="text-slate-400 italic">No text update</span>
                              )}
                              {updates.length > 1 && (
                                <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 dark:bg-slate-800 text-slate-500 font-medium">
                                  +{updates.length - 1} updates
                                </span>
                              )}
                            </td>

                            {/* Action */}
                            <td className="py-3.5 px-4 text-right whitespace-nowrap">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSelectedIncidentId(incident.id)}
                                className="h-7 text-xs text-[#072340] dark:text-[#80CCB8] hover:bg-[#80CCB8]/10"
                              >
                                View Timeline
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-8 mt-16 text-xs text-slate-500 dark:text-slate-400">
        <div className="container mx-auto px-4 max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-1">
            <span>© 2026</span>
            </div>

          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
            <a
              href="https://www.upstreamtech.io/legal"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              Terms of Service
            </a>
            <a
              href="https://www.upstreamtech.io/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="mailto:support@upstreamtech.io"
              className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
              >
              support@upstreamtech.io
            </a>
              <a
            href="tel:+611300366165"
            className="hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
              >
              +611300366165
              </a>
              </div>
              </div>
            </footer>

      {/* Incident Details Modal */}
      <Dialog open={!!selectedIncidentId} onOpenChange={(open) => !open && setSelectedIncidentId(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 border-4 border-[#072340] border-t-[#80CCB8] rounded-full animate-spin" />
            </div>
          ) : incidentDetail ? (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between gap-4 pr-6">
                  <DialogTitle className="text-lg font-bold text-slate-900 dark:text-slate-100">
                    {incidentDetail.name}
                  </DialogTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopyDirectLink(incidentDetail.id)}
                    className="shrink-0 gap-1.5 text-xs font-medium border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    {copiedLink ? (
                      <>
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                        <span className="text-emerald-600">Copied Link!</span>
                      </>
                    ) : (
                      <>
                        <Share2 className="h-3.5 w-3.5 text-slate-500" />
                        <span>Copy Share Link</span>
                      </>
                    )}
                  </Button>
                </div>
              </DialogHeader>
              <div className="space-y-5 pt-2">
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/60 rounded-lg">
                  {getStatusIcon(incidentDetail.status, incidentDetail.serviceStatus)}
                  <div>
                    <p className="font-semibold text-sm">
                      {getStatusLabel(incidentDetail.status, incidentDetail.serviceStatus)}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Incident ID: {incidentDetail.id}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg">
                    <p className="text-slate-400 font-medium mb-1">
                      {incidentDetail.status === 'Maintenance' && incidentDetail.incidentStart && new Date(incidentDetail.incidentStart) > new Date()
                        ? 'Scheduled Start'
                        : 'Started'}
                    </p>
                    <p className="font-semibold text-slate-800 dark:text-slate-200">
                      {incidentDetail.incidentStart
                        ? new Date(incidentDetail.incidentStart).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Unknown'}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg">
                    <p className="text-slate-400 font-medium mb-1">Status</p>
                    <p className="font-semibold text-slate-800 dark:text-slate-200">
                      {incidentDetail.status ?? 'Investigating'}
                    </p>
                  </div>
                  {incidentDetail.serviceType && incidentDetail.serviceType.length > 0 && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg col-span-2 sm:col-span-1">
                      <p className="text-slate-400 font-medium mb-1">Affected Service</p>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">
                        {incidentDetail.serviceType.join(', ')}
                      </p>
                    </div>
                  )}
                  {incidentDetail.subServiceType && incidentDetail.subServiceType.length > 0 && (
                    <div className="p-3 bg-slate-50 dark:bg-slate-800/40 rounded-lg col-span-2 sm:col-span-1">
                      <p className="text-slate-400 font-medium mb-1">Sub-service / Region</p>
                      <p className="font-semibold text-slate-800 dark:text-slate-200">
                        {incidentDetail.subServiceType.join(', ')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Updates timeline */}
                <div>
                  <h4 className="font-semibold text-sm mb-3 text-slate-900 dark:text-slate-100">
                    Incident Updates Timeline
                  </h4>
                  {incidentDetail.updates && incidentDetail.updates.length > 0 ? (
                    <div className="space-y-3.5 border-l-2 border-slate-200 dark:border-slate-700 ml-2 pl-4">
                      {incidentDetail.updates.map((update) => (
                        <div key={update.id} className="relative space-y-1">
                          <div className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-[#80CCB8]" />
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                              {update.creator?.name ?? 'Upstream Operations'}
                            </span>
                            <span className="text-slate-400">•</span>
                            <span className="text-slate-400">
                              {formatRelativeTime(update.created_at ? new Date(update.created_at) : null)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                            {update.text_body || 'Status update logged.'}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic bg-slate-50 dark:bg-slate-800/40 p-3 rounded-lg">
                      No update comments logged for this incident yet.
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
